'use client';

import React, { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import dynamic from 'next/dynamic';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from '@ag-grid-community/theming';
import { iconSetAlpine } from 'ag-grid-community';
import { HotKeys } from './hot-keys';
import { TrashIcon, ListPlusIcon, NotePencilIcon, SortAscendingIcon, SortDescendingIcon, CaretDownIcon, MagicWandIcon, CheckIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { BLANK_SLIDE, PPTX_POLL_INTERVAL_MS, PPTX_POLL_MAX_ATTEMPTS } from '@/shared/constants';
import { GetEventsFromEntityService, Slide, View, Source } from '@/shared/types';
import { Spinner } from '@heroui/spinner';
import { sortByRealDatetime } from '@/shared/clientFunctions';
// Table cell renderers
import Dates from './cells/dates';
import Visibility from './cells/visibility';
import Titles from './cells/titles';
import Descriptions from './cells/descriptions';
import { evidenceTypeValues } from './cells/evidenceTypes';
import Times from './cells/times';
import Media from './cells/media';
import SimplifiedSources from './cells/simplifiedSources';
import Actions from './cells/actions';
import DisplayOrder from './cells/displayOrder';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getTimezoneListByName, recordStat, sanitizeHttpUrlForBrowserLoad } from '@/shared/functions';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import {
  createTimelinePptxJob,
  downloadTimelinePptxArtifact,
  getTimelinePptxJobStatus,
  listTimelinePptxJobs,
  type PptxJobResponse,
} from '@/shared/cors-timeline-api';
import { StatsContext, ToastContext } from '@/app/contexts';
import { useOptionalI18n } from '@/i18n/LocaleProvider';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { useGridLocaleText } from '@/i18n/useGridLocaleText';
import { AuthContext } from '@/app/contexts';
import { ToastLevel } from './ui/toast';
import type { EnrichEventOptions } from './eventModal';
import type { TpTableConfirmDialogsProps } from './tp-table-confirm-dialogs';
import type { PptxExportModalProps } from './pptxExportModal';

const TpTableConfirmDialogs = dynamic<TpTableConfirmDialogsProps>(
  () => import('./tp-table-confirm-dialogs').then((m) => m.TpTableConfirmDialogs),
  { ssr: false, loading: () => null },
);
const PptxExportModal = dynamic<PptxExportModalProps>(
  () => import('./pptxExportModal').then((m) => m.PptxExportModal),
  { ssr: false, loading: () => null },
);
const EventsReviewModal = dynamic(() => import('./events-review-modal'), {
  ssr: false,
  loading: () => null,
});

dayjs.extend(utc);
dayjs.extend(timezone);

export const columnEditMappings: Record<string, { columnName: string, options: string[] }> = {
  'evidenceType': { columnName: 'Evidence Type', options: evidenceTypeValues.filter((v): v is string => v !== undefined) },
  'slideDescription': { columnName: 'Description', options: [] },
  'slideSubTitle': { columnName: 'Subtitle', options: [] },
  'slideTitle': { columnName: 'Title', options: [] },
  'media': { columnName: 'Media', options: [] },
  'sources': { columnName: 'Sources', options: [] },
  'actions': { columnName: '', options: [] },
  'date': { columnName: 'Date', options: [] },
  'time': { columnName: 'Time', options: [] },
};

const myTheme = themeQuartz
	.withPart(iconSetAlpine)
	.withParams({
    accentColor: "#FFFFFF54",
    backgroundColor: "#0F1D32",
    borderColor: "#FFFFFF12",
    borderRadius: 20,
    browserColorScheme: "dark",
    cellHorizontalPaddingScale: 0.5,
    checkboxBorderRadius: "3px",
    chromeBackgroundColor: {
        ref: "backgroundColor"
    },
    columnBorder: false,
    fontFamily: "inherit",
    fontSize: "14px",
    foregroundColor: "#FCFCFC",
    headerBackgroundColor: "#09121E",
    headerFontSize: 16,
    headerFontWeight: 500,
    headerTextColor: "#FFFFFF",
    headerVerticalPaddingScale: .5,
    headerColumnResizeHandleColor: "#FFFFFF12",
    headerColumnResizeHandleWidth: 3,
    headerColumnResizeHandleHeight: 20,
    iconSize: "16px",
    inputBorder: false,
    inputBorderRadius: "0px",
    inputFocusBackgroundColor: "#FFFFFF00",
    inputFocusBorder: false,
    inputFocusShadow: "#0000002A 0px 0px 0px 3px",
    inputFocusTextColor: "#FCFCFC",
    inputTextColor: "#FCFCFC",
    oddRowBackgroundColor: "#FFFFFF10",
    rowBorder: false,
    rowVerticalPaddingScale: .5,
    sidePanelBorder: true,
    spacing: 8,
    wrapperBorder: true,
    wrapperBorderRadius: "7px"
});

// Interfaces and types
interface TPTableProps {
  allSlides: Slide[];
  caseId: string;
  timelineId: string;
  title?: string;
  description?: string;
  media?: string;
  updateTimeline: (action: 'delete' | 'update' | 'order', rows: Slide[]) => void;
  onOpenModal: (newEvent: Slide, column?: string | null) => void;
  onSetEventIndex: (index: number) => void;
  onEditEvent: (updatedEvent: Slide) => void;
  isEditableTimeline: boolean;
  onAddEvent: (newEvent: Slide) => void;
  onDeleteEvent: (event: Slide) => void;
  onDeleteTimeline: () => void;
  sortByDateTime: boolean;
  enrichEvents: (parsedEvents: Slide[], useEnrichment: boolean, edit: boolean, timelineIdIn?: string, enrichOptions?: EnrichEventOptions) => Promise<void>;
  createEventsFromParsedEvents: (parsedEvents: Slide[], enriching: boolean, edit: boolean, timelineIdIn?: string, enrichOptions?: EnrichEventOptions) => Promise<void>;
  entityExtractionJobs: any[];
  getEventsFromEntityService: GetEventsFromEntityService;
  createEventsFromEntityEvents: (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => Promise<void>;
  featureFlags?: { [key: string]: any };
  onViewChange?: (view: View) => void;
  onBackToWorkProducts?: () => void;
  selectedView?: View;
  onOpenTitleSlideModal: () => void;
}

interface StagedChange {
  slide: number;
  id: string;
  column: string;
  value: string;
}

interface EditingItems {
  editor: string;
  slide: number;
  columnDisplayName: string;
  column: string;
  items: Array<{ id: string; value: any }>;
}

const getSlideRowId = (slide?: Slide | null) => {
  if (slide?.id) return String(slide.id);
  return String(slide?.displayOrder ?? '');
};

const DateAdded = ({ value }: { value: string }) => {
  return <span>{dayjs(value).format('MM/DD/YYYY HH:mm:ss')}</span>;
}

// Component definition
const TPTable: React.FC<TPTableProps> = ({
  title,
  caseId,
  timelineId,
  description,
  media,
  allSlides,
  updateTimeline,
  onOpenModal,
  onSetEventIndex,
  onEditEvent,
  isEditableTimeline,
  onAddEvent,
  onDeleteEvent,
  onDeleteTimeline,
  sortByDateTime,
  enrichEvents,
  createEventsFromParsedEvents,
  entityExtractionJobs,
  getEventsFromEntityService,
  createEventsFromEntityEvents,
  featureFlags,
  onViewChange,
  onBackToWorkProducts,
  selectedView,
  onOpenTitleSlideModal,
}) => {
  const t = useTimePilotT();
  const locale = useOptionalI18n()?.locale;
  const gridLocaleText = useGridLocaleText();
  // State declarations
  const [editingItems, setEditingItems] = useState<EditingItems | null>(null);
  const [stagedChanges, setStagedChanges] = useState<Record<string, StagedChange>>({});
  const [deleteTimelineDialogOpen, setDeleteTimelineDialogOpen] = useState(false);
  const [deleteSelectedEventsDialogOpen, setDeleteSelectedEventsDialogOpen] = useState(false);
  const [enrichSelectedEventsDialogOpen, setEnrichSelectedEventsDialogOpen] = useState(false);
  const [selectedSortColumn, setSelectedSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>('none');
  const [selectedRows, setSelectedRows] = useState<Slide[]>([]);
  const [enrichSelectedRows, setEnrichSelectedRows] = useState<Slide[]>([]);
  const [enrichSelectedUseEventSourcesAsScope, setEnrichSelectedUseEventSourcesAsScope] = useState(false);
  const [enrichSelectedKeepCurrentMedia, setEnrichSelectedKeepCurrentMedia] = useState(true);
  const [currentEvent, setCurrentEvent] = useState<Slide | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [applyOrderEnabled, setApplyOrderEnabled] = useState(false);
  const [extractionJobs, setExtractionJobs] = useState<any[]>([]);
  const [eventsReviewModalOpen, setEventsReviewModalOpen] = useState(false);
  const [eventsReviewModalQuestion, setEventsReviewModalQuestion] = useState('');
  const [eventsReviewModalExtractionJob, setEventsReviewModalExtractionJob] = useState<any>(null);
  const [loadingExtractedEvents, setLoadingExtractedEvents] = useState(false);
  const [extractedEventsPopoverOpen, setExtractedEventsPopoverOpen] = useState(false);
  const [editSelectedMenuOpen, setEditSelectedMenuOpen] = useState(false);
  const [sortColumnMenuOpen, setSortColumnMenuOpen] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  const [pptxExportModalOpen, setPptxExportModalOpen] = useState(false);
  const [cachedPptxJobs, setCachedPptxJobs] = useState<PptxJobResponse[]>([]);
  const [loadingCachedPptxJobs, setLoadingCachedPptxJobs] = useState(false);
  const [downloadingPptxJobIds, setDownloadingPptxJobIds] = useState<Set<string>>(new Set());
  const pptxPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Bumped when export ends or the component unmounts so stale poll callbacks no-op. */
  const pptxPollSessionRef = useRef(0);

  const statsContext = useContext(StatsContext);
  const authContext = useContext(AuthContext);
  const { setToast, setToastOpen } = useContext(ToastContext);

  const showPptxToast = useCallback((options: {
    title: string;
    description: string;
    level: ToastLevel;
    duration?: number;
  }) => {
    setToast({
      setOpen: setToastOpen,
      open: true,
      location: 'bottom-right',
      time: Date.now(),
      duration: options.duration ?? 15000,
      title: options.title,
      description: options.description,
      level: options.level,
    });
  }, [setToast, setToastOpen]);

  const readTimelineAuth = useCallback(() => {
    const idToken = authContext.getIdToken();
    const realAccessToken = authContext.getRealAccessToken();
    const cellId = resolveCellIdFromIdToken(idToken);
    if (!cellId || !idToken || !realAccessToken) {
      throw new Error('Missing cell session for timeline export.');
    }
    return { cellId, idToken, realAccessToken };
  }, [authContext]);

  useEffect(() => {
    if (allSlides && allSlides.length > 0 && !currentEvent) {
      setCurrentEvent(allSlides[0]);
    }
  }, [allSlides, currentEvent]);

  const extractionJobsPropRef = useRef(entityExtractionJobs);
  extractionJobsPropRef.current = entityExtractionJobs;
  const extractedEventsRefreshRef = useRef(0);

  useEffect(() => {
    setExtractionJobs(entityExtractionJobs || []);
    setLoadingExtractedEvents(false);
  }, [entityExtractionJobs]);

  const refreshExtractedEvents = async () => {
    const generation = ++extractedEventsRefreshRef.current;
    const jobsBeforeRefresh = extractionJobsPropRef.current;
    setLoadingExtractedEvents(true);
    try {
      await getEventsFromEntityService('GET_ALL_JOBS', '', caseId, '', [], 0, null, false, 'user');
    } finally {
      // The jobs effect copies the new list and clears Checking in the same
      // commit. Clearing here runs first and paints "No Extracted Events" or
      // the previous list for a frame. Only clear ourselves when the prop
      // never changed (early return or error).
      window.setTimeout(() => {
        if (extractedEventsRefreshRef.current !== generation) return;
        if (extractionJobsPropRef.current === jobsBeforeRefresh) {
          setLoadingExtractedEvents(false);
        }
      }, 0);
    }
  };

  const openExtractedEvents = () => {
    setExtractedEventsPopoverOpen(true);
    void refreshExtractedEvents();
  };

  const handleCloseEventsReviewModal = async (open: boolean) => {
    if (open) {
      setEventsReviewModalOpen(true);
    } else {
      setEventsReviewModalOpen(false);
      setEventsReviewModalQuestion('');
      setEventsReviewModalExtractionJob(null);
      await refreshExtractedEvents();
    }
  };

  const getExtractionJobId = (job: any, fallbackIndex: number) => {
    return String(job?.jobId ?? job?.id ?? job?.job_id ?? fallbackIndex);
  };

  const getExtractionJobQuestion = (job: any, fallbackIndex: number) => {
    const question = typeof job?.question === 'string' ? job.question.trim() : '';
    if (!question) return `Extraction Job ${fallbackIndex + 1}`;
    return question.length > 250 ? `${question.slice(0, 250)}...` : question;
  };

  const isExtractionJobPending = (job: any) => {
    return String(job?.status ?? '').toLowerCase() === 'pending';
  };

  const handleOpenExtractionJob = (job: any, fallbackIndex: number) => {
    setEventsReviewModalQuestion(getExtractionJobQuestion(job, fallbackIndex));
    setEventsReviewModalExtractionJob(job);
    setEventsReviewModalOpen(true);
    setExtractedEventsPopoverOpen(false);
  };

  const handleVisibilityClick = useCallback((params: any) => {
    const index = params.data.displayOrder - 1;
    const event = allSlides[index];
    event.show = !event.show;
    // `event.show` is mutated in place, so the `allSlides` reference (and every
    // memo prop) is unchanged. AG Grid manages its own cell rendering and runs
    // change detection only when `rowData` changes, so neither a parent
    // re-render nor `React.memo(TPTable)` will repaint the eye icon. Explicitly
    // refresh the toggled row's visibility cell so it reflects the new state immediately.
    const api = params.api ?? gridRef.current?.api;
    const rowNode = params.node ?? api?.getRowNode(getSlideRowId(params.data));
    if (api && rowNode) {
      api.refreshCells({ rowNodes: [rowNode], columns: ['visibility'], force: true });
    }
    onEditEvent(event);
  }, [allSlides, onEditEvent]);

  const timezoneListByName = getTimezoneListByName();

  const handleEnrichEvents = async (eventsToEnrich: Slide[], enrichOptions: EnrichEventOptions = {}) => {
    await enrichEvents(eventsToEnrich as Slide[], true, true, undefined, enrichOptions);
  }

  const handleCreateEvent = () => {
    if (isEditableTimeline) {
      const newEvent = { ...BLANK_SLIDE, id: `NEW_EVENT_FROM_TABLE`, displayOrder: allSlides.length + 1 };
      onOpenModal(newEvent, 'create');
    }
  }
  const handleDuplicateEvent = useCallback((displayOrder: string) => {
    const index = Number(displayOrder) - 1;
    onSetEventIndex(index);
    const duplicate = { ...allSlides[index], show: true, id: `NEW_DUPLICATE_FROM_TABLE_${index}`};
    onOpenModal(duplicate, 'duplicate');
  }, [allSlides, onAddEvent]);

  const handleEditEvent = useCallback((displayOrder: string, column?: string | null) => {
    const index = Number(displayOrder) - 1;
    onSetEventIndex(index);
    onOpenModal({ ...allSlides[index] }, column);
  }, [allSlides, onOpenModal]);

  const handleEditTitleSlide = () => {
    onOpenTitleSlideModal();
  }

  const handleDeleteEvent = useCallback((displayOrder: string) => {
    const index = Number(displayOrder) - 1;
    onSetEventIndex(index);
    onDeleteEvent(allSlides[index]);
  }, [allSlides, onDeleteEvent]);

  const handleDeleteTimeline = () => {
    setDeleteTimelineDialogOpen(false);
    onDeleteTimeline();
  }

  const fetchCachedPptxExports = useCallback(async () => {
    setLoadingCachedPptxJobs(true);
    try {
      const { cellId, idToken, realAccessToken } = readTimelineAuth();
      const listData = await listTimelinePptxJobs({
        cellId,
        accessToken: realAccessToken,
        idToken,
        timelineId,
      });
      setCachedPptxJobs(listData.jobs ?? []);
    } catch (error) {
      console.error('Error loading cached PPTX exports:', error);
      showPptxToast({
        title: t('timeline.export.loadFailed'),
        description: (error as Error).message,
        level: ToastLevel.ERROR,
      });
      setCachedPptxJobs([]);
    } finally {
      setLoadingCachedPptxJobs(false);
    }
  }, [readTimelineAuth, showPptxToast, timelineId]);

  const clearPptxPollTimeout = useCallback(() => {
    if (pptxPollTimeoutRef.current != null) {
      clearTimeout(pptxPollTimeoutRef.current);
      pptxPollTimeoutRef.current = null;
    }
  }, []);

  const finishPptxExport = useCallback(() => {
    clearPptxPollTimeout();
    setExportingPptx(false);
  }, [clearPptxPollTimeout]);

  const downloadPptxFile = useCallback(async (timelineId: string, jobId: string, timelineTitle?: string) => {
    try {
      const { cellId, idToken, realAccessToken } = readTimelineAuth();
      // Use timeline title for filename
      const sanitizedTitle = timelineTitle?.replace(/[^a-zA-Z0-9-_ ]/g, '_') || `timeline-${timelineId}`;
      const filename = `${sanitizedTitle}.zip`;

      const { url } = await downloadTimelinePptxArtifact({
        cellId,
        accessToken: realAccessToken,
        idToken,
        timelineId,
        jobId,
        filename,
      });

      // The presigned URL points directly at S3, which returns the bundle with
      // a Content-Disposition: attachment header, so the browser downloads it.
      const anchor = document.createElement('a');
      anchor.href = sanitizeHttpUrlForBrowserLoad(url);
      anchor.download = filename;
      anchor.click();
    } catch (error) {
      console.error('Error downloading PPTX:', error);
      throw error;
    }
  }, [readTimelineAuth]);

  const pollPptxJobStatus = useCallback(
    async (jobId: string, attempt: number, session: number) => {
      if (session !== pptxPollSessionRef.current) {
        return;
      }
      if (attempt >= PPTX_POLL_MAX_ATTEMPTS) {
        finishPptxExport();
        showPptxToast({
          title: t('timeline.export.stillInProgress'),
          description: t('timeline.export.stillInProgressDescription'),
          level: ToastLevel.INFO,
          duration: 30000,
        });
        void fetchCachedPptxExports();
        return;
      }
      try {
        const { cellId, idToken, realAccessToken } = readTimelineAuth();
        const jobData = await getTimelinePptxJobStatus({
          cellId,
          accessToken: realAccessToken,
          idToken,
          timelineId,
          jobId,
        });

        if (session !== pptxPollSessionRef.current) {
          return;
        }

        const status = String(jobData.status).toUpperCase();

        if (status === 'COMPLETED') {
          try {
            await downloadPptxFile(timelineId, jobId, title);
            finishPptxExport();
            showPptxToast({
              title: t('timeline.export.ready'),
              description: t('timeline.export.downloadStartedDescription'),
              level: ToastLevel.SUCCESS,
            });
            void fetchCachedPptxExports();
          } catch (downloadError) {
            console.error('Error downloading PPTX file:', downloadError);
            finishPptxExport();
            showPptxToast({
              title: t('timeline.export.downloadFailed'),
              description: (downloadError as Error).message,
              level: ToastLevel.ERROR,
            });
          }
          return;
        }

        if (status === 'FAILED') {
          finishPptxExport();
          showPptxToast({
            title: t('timeline.export.failed'),
            description: jobData.errorMessage || t('timeline.export.unknownError'),
            level: ToastLevel.ERROR,
          });
          return;
        }

        if (status === 'PENDING' || status === 'PROCESSING') {
          pptxPollTimeoutRef.current = setTimeout(() => {
            void pollPptxJobStatus(jobId, attempt + 1, session);
          }, PPTX_POLL_INTERVAL_MS);
          return;
        }

        finishPptxExport();
        showPptxToast({
          title: t('timeline.export.unexpectedStatus'),
          description: t('timeline.export.unexpectedStatusDescription', { status: jobData.status }),
          level: ToastLevel.ERROR,
        });
      } catch (error) {
        console.error('Error polling PPTX job status:', error);
        finishPptxExport();
        showPptxToast({
          title: t('timeline.export.statusCheckFailed'),
          description: (error as Error).message,
          level: ToastLevel.ERROR,
        });
      }
    },
    [
      readTimelineAuth,
      timelineId,
      title,
      downloadPptxFile,
      finishPptxExport,
      showPptxToast,
      fetchCachedPptxExports,
    ],
  );

  const startPptxJobPolling = useCallback(
    (jobId: string) => {
      clearPptxPollTimeout();
      const session = pptxPollSessionRef.current;
      pptxPollTimeoutRef.current = setTimeout(() => {
        void pollPptxJobStatus(jobId, 1, session);
      }, PPTX_POLL_INTERVAL_MS);
    },
    [clearPptxPollTimeout, pollPptxJobStatus],
  );

  useEffect(() => {
    return () => {
      pptxPollSessionRef.current += 1;
      clearPptxPollTimeout();
    };
  }, [clearPptxPollTimeout]);

  const handleOpenPptxExportModal = () => {
    setPptxExportModalOpen(true);
    void fetchCachedPptxExports();
  };

  const handleDownloadCachedPptx = async (jobId: string) => {
    setDownloadingPptxJobIds((prev) => new Set(prev).add(jobId));
    try {
      await downloadPptxFile(timelineId, jobId, title);
      showPptxToast({
        title: t('timeline.export.downloadStarted'),
        description: t('timeline.export.downloadStartedDescription'),
        level: ToastLevel.SUCCESS,
      });
    } catch (error) {
      console.error('Error downloading cached PPTX:', error);
      showPptxToast({
        title: t('timeline.export.downloadFailed'),
        description: (error as Error).message,
        level: ToastLevel.ERROR,
      });
    } finally {
      setDownloadingPptxJobIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleGeneratePptx = async () => {
    pptxPollSessionRef.current += 1;
    clearPptxPollTimeout();
    setExportingPptx(true);

    try {
      const { cellId, idToken, realAccessToken } = readTimelineAuth();
      const jobData = await createTimelinePptxJob({
        cellId,
        accessToken: realAccessToken,
        idToken,
        timelineId,
      });

      if (!jobData.id) {
        throw new Error('Job ID not found in response');
      }

      const status = String(jobData.status).toUpperCase();

      if (status === 'COMPLETED') {
        try {
          await downloadPptxFile(timelineId, jobData.id, title);
          finishPptxExport();
          showPptxToast({
            title: t('timeline.export.ready'),
            description: t('timeline.export.downloadStartedDescription'),
            level: ToastLevel.SUCCESS,
          });
          void fetchCachedPptxExports();
        } catch (downloadError) {
          console.error('Error downloading PPTX file:', downloadError);
          finishPptxExport();
          showPptxToast({
            title: t('timeline.export.downloadFailed'),
            description: (downloadError as Error).message,
            level: ToastLevel.ERROR,
          });
        }
      } else if (status === 'PENDING' || status === 'PROCESSING') {
        showPptxToast({
          title: t('timeline.export.generating'),
          description: t('timeline.export.generatingDescription'),
          level: ToastLevel.INFO,
          duration: 30000,
        });
        startPptxJobPolling(jobData.id);
      } else if (status === 'FAILED') {
        finishPptxExport();
        throw new Error(jobData.errorMessage || 'PPTX generation failed');
      } else {
        console.warn(`Unknown PPTX job status: ${jobData.status}, starting polling`);
        startPptxJobPolling(jobData.id);
      }
    } catch (error) {
      console.error('Error creating PPTX job:', error);
      finishPptxExport();
      showPptxToast({
        title: t('timeline.export.generateFailed'),
        description: (error as Error).message,
        level: ToastLevel.ERROR,
      });
    }
  };

  const handleRowActions = useCallback((displayOrder: string, action: string) => {
    if (action === 'Edit Event') {
      handleEditEvent(displayOrder);
    } else if (action === 'Duplicate Event') {
      handleDuplicateEvent(displayOrder);
    } else if (action === 'Delete Event') {
      handleDeleteEvent(displayOrder);
    }
  }, [handleEditEvent, handleDuplicateEvent, handleDeleteEvent]);

  const defaultColDef = {
    sortable: true,
    filter: true,
    editable: false,
    onCellDoubleClicked: () => {},
  };

  const sortByRealDatetimeComparator = (date1: string, date2: string, node1: any, node2: any) => {
    return sortByRealDatetime(node1.data, node2.data, timezoneListByName);
  }

  const handleSelectionChanged = (params: any) => {
    const selectedEvents = params.api.getSelectedNodes().map((node: any) => node.data);
    const newCurrentEvent = selectedEvents.length > 0 ? selectedEvents[0] : null;
    setCurrentEvent(newCurrentEvent);
    setSelectedRows(selectedEvents);
  }

  const handleDeleteSelectedEvents = () => {
    updateTimeline('delete', selectedRows);
  }

  const getSelectedGridRows = () => {
    const api = gridRef.current?.api;
    if (!api) return selectedRows;
    return api.getSelectedNodes().map((node: any) => node.data);
  };

  const handleOpenEnrichSelectedEventsDialog = () => {
    const rowsToEnrich = getSelectedGridRows();
    if (rowsToEnrich.length === 0) {
      setSelectedRows([]);
      return;
    }
    setEnrichSelectedRows([...rowsToEnrich]);
    setEnrichSelectedEventsDialogOpen(true);
  };

  const handleConfirmEnrichSelectedEvents = async () => {
    const rowsToEnrich = enrichSelectedRows;
    setEnrichSelectedEventsDialogOpen(false);
    setEnrichSelectedRows([]);
    if (rowsToEnrich.length === 0) return;
    await handleEnrichEvents(rowsToEnrich, {
      useEventSourcesAsScope: enrichSelectedUseEventSourcesAsScope,
      keepCurrentMedia: enrichSelectedKeepCurrentMedia,
    });
  };

  const handleEnrichSelectedEventsDialogOpenChange = (open: boolean) => {
    setEnrichSelectedEventsDialogOpen(open);
    if (!open) {
      setEnrichSelectedRows([]);
    }
  };

  const dragHandleClass = '[&_.ag-icon-grip::before]:h-[74px] [&_.ag-icon-grip::before]:bg-[linear-gradient(180deg,rgba(255,255,255,1)0px,rgba(15,29,50,1)70px)]';

  const columnDefs = useMemo(() => {
    const columnDefsForEditing = [
      {
        colId: 'rowDragHandle',
        width: 10,
        rowDrag: sortByDateTime ? false : true,
        sortable: false,
        filter: false,
        cellClass: dragHandleClass,
      },
      { field: 'actions', width: '65px', headerName: t('timeline.table.column.tools'), filter: false, sortable: false, cellRenderer: Actions, cellRendererParams: { handleRowActions: handleRowActions } },
    ];
    const columnDefs = [
      { field: 'visibility', width: '35px', headerName: '', sortable: false, cellRenderer: Visibility, onCellClicked: handleVisibilityClick },
      { field: 'displayOrder', width: '95px', headerName: t('timeline.table.column.order'), rowDrag: false, sortable: true, editable: false, filter: false, cellRenderer: DisplayOrder },
      {
        field: 'media',
        width: 120,
        headerName: t('timeline.table.column.media'),
        cellRenderer: Media,
        cellStyle: {
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          padding: '0',
        },
        onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'media'),
      },
      { field: 'date', width: 116, headerName: t('timeline.table.column.date'), sortable: true, cellRenderer: Dates, comparator: sortByRealDatetimeComparator, singleClickEdit : true, onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'date') },
      { field: 'time', width: 110, headerName: t('timeline.table.column.time'), sortable: false, cellRenderer: Times, onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'time') },
      { field: 'slideSubTitle', width: 100, headerName: t('timeline.table.column.header'), wrapText: true, sortable: true,cellRenderer: Descriptions, onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'slideSubTitle') },
      { field: 'slideTitle', width: 250, headerName: t('timeline.table.column.title'), wrapText: true, sortable: true,cellRenderer: Titles, onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'slideTitle') },
      { field: 'slideDescription', minWidth: 280, flex: 1, headerName: t('timeline.table.column.description'), wrapText: true, sortable: true, cellRenderer: Descriptions, onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'slideDescription') },
      { field: 'sources', width: 180, headerName: t('timeline.table.column.sources'), sortable: true, cellRenderer: SimplifiedSources, onCellClicked: (params: any) => handleEditEvent(params.data.displayOrder, 'sources') },
      { field: 'updatedAt', width: 180, headerName: t('timeline.table.column.dateUpdated'), cellRenderer: DateAdded, },
    ];
    return !isEditableTimeline ? columnDefs : [...columnDefsForEditing, ...columnDefs];
  }, [handleEditEvent, handleRowActions, handleVisibilityClick, isEditableTimeline, featureFlags, dragHandleClass, t]);

  // AG Grid keeps the header labels from the first columnDefs it applied.
  // The first paint is English, so a later language change has to refresh
  // the header row or "Date" / "Title" stay English while the cells update.
  useEffect(() => {
    gridRef.current?.api?.refreshHeader();
  }, [locale, columnDefs]);

  const handleCloseModal = () => {
    onSetEventIndex(0);
    setEditingItems(null);
  };

  const getRowId = useCallback(function (params: any) {
    return getSlideRowId(params.data);
  }, []);

  const handleRowDragEnd = (params: any) => {
    const newRows: Slide[] = [];
    params.api.forEachNode((node: any) => {
      newRows.push(node.data);
    });
    updateTimeline('order', newRows);
  }

  const handleApplyOrder = () => {
    const newRows: Slide[] = [];
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node: any) => {
      newRows.push(node.data);
    });
    updateTimeline('order', newRows);
  }

  const checkDisplayOrder = useCallback(() => {
    if (!gridRef.current?.api) return;

    // Get the sort state of all columns
    const columnState = gridRef.current.api.getColumnState();

    // Find if any column is sorted
    const sortedColumn = columnState.find(col => col.sort);

    // Only show Apply Order button if:
    // 1. A column other than displayOrder is sorted, or
    // 2. displayOrder column is sorted but not ascending
    setApplyOrderEnabled(
      !!sortedColumn && (
        sortedColumn.colId !== 'displayOrder' ||
        sortedColumn.sort !== 'asc'
      )
    );
  }, []);

  const handleSortChanged = (params: any) => {
    checkDisplayOrder();

    // Get current sort state and update dropdown/button
    const sortModel = params.api.getColumnState().find((col: any) => col.sort);
    if (sortModel) {
      // Find the column definition to get the header name
      const colDef = columnDefs.find(col => col.field === sortModel.colId);
      setSelectedSortColumn(colDef?.field || sortModel.colId || null);
      setSortDirection(sortModel.sort as 'asc' | 'desc');
    } else {
      // No sorting applied - clear both states
      setSortDirection('none');
      setSelectedSortColumn(null);
    }
  };

  const handleSortColumnSelect = (column: string) => {
    setSelectedSortColumn(column);
    setSortDirection('asc'); // Set to ascending by default when column is selected
    if (gridRef.current?.api) {
      const colDef = columnDefs.find(col => col.field === column);
      if (colDef) {
        // Apply ascending sort when column is selected
        gridRef.current.api.applyColumnState({
          state: [{ colId: colDef.field || '', sort: 'asc' }],
          defaultState: { sort: null }
        });
        checkDisplayOrder();
      }
    }
  };

  const handleSortDirectionToggle = () => {
    // Cycle through: asc -> desc -> none -> asc
    const newDirection = sortDirection === 'asc' ? 'desc' :
                        sortDirection === 'desc' ? 'none' : 'asc';
    setSortDirection(newDirection);
    if (gridRef.current?.api && selectedSortColumn) {
      const colDef = columnDefs.find(col => col.field === selectedSortColumn);
      if (colDef) {
        if (newDirection === 'none') {
          // Clear sorting
          gridRef.current.api.applyColumnState({
            defaultState: { sort: null }
          });
        } else {
          // Apply new sort direction
          gridRef.current.api.applyColumnState({
            state: [{ colId: colDef.field || '', sort: newDirection }],
            defaultState: { sort: null }
          });
        }
        checkDisplayOrder();
      }
    }
  };

  const getTitleSlideTitle = () => {
    if (!title) return 'ADD TITLE SLIDE';
    return title.length > 25 ? `${title.substring(0, 25)}...` : title;
  };

  return (
    <div data-testid="timelineTableView" className={`flex flex-col h-full w-full p-0`}>
      <HotKeys onCloseModal={handleCloseModal} isModal={!!editingItems} />
      <TpTableConfirmDialogs
        deleteTimelineDialogOpen={deleteTimelineDialogOpen}
        setDeleteTimelineDialogOpen={setDeleteTimelineDialogOpen}
        onDeleteTimeline={handleDeleteTimeline}
        deleteSelectedEventsDialogOpen={deleteSelectedEventsDialogOpen}
        setDeleteSelectedEventsDialogOpen={setDeleteSelectedEventsDialogOpen}
        onDeleteSelectedEvents={handleDeleteSelectedEvents}
        enrichSelectedEventsDialogOpen={enrichSelectedEventsDialogOpen}
        setEnrichSelectedEventsDialogOpen={handleEnrichSelectedEventsDialogOpenChange}
        enrichSelectedUseEventSourcesAsScope={enrichSelectedUseEventSourcesAsScope}
        setEnrichSelectedUseEventSourcesAsScope={setEnrichSelectedUseEventSourcesAsScope}
        enrichSelectedKeepCurrentMedia={enrichSelectedKeepCurrentMedia}
        setEnrichSelectedKeepCurrentMedia={setEnrichSelectedKeepCurrentMedia}
        onEnrichSelectedEvents={handleConfirmEnrichSelectedEvents}
      />
      <PptxExportModal
        isOpen={pptxExportModalOpen}
        onOpenChange={setPptxExportModalOpen}
        cachedJobs={cachedPptxJobs}
        loadingCachedJobs={loadingCachedPptxJobs}
        generating={exportingPptx}
        downloadingJobIds={downloadingPptxJobIds}
        onGenerateNew={() => {
          void handleGeneratePptx();
        }}
        onDownloadCached={(jobId) => {
          void handleDownloadCachedPptx(jobId);
        }}
      />
      <div className='flex'>
        <main className='flex flex-col'>
          <div className='ag-theme-quartz-dark w-[100vw] h-[100vh] sm:w-[calc(100vw-60px)] sm:h-[calc(100vh-125px)]'>
            {!allSlides && (
              <div className='flex flex-col h-[calc(100vh-62px)] items-center justify-center overflow-hidden'>
                <h1 className="text-4xl text-white">{t('timeline.table.selectTimeline')}</h1>
              </div>
            )}
            {!!allSlides && (
              <div className={`ag-theme-quartz-dark w-[100vw] sm:w-[calc(100vw-60px)] ${(featureFlags?.enableMediaAndSourceShare ? 'sm:h-[calc(100vh-299px)]' : 'sm:h-[calc(100vh-260px)]') + ' h-[calc(100vh-150px)]'}`}>
                {isEditableTimeline && (
                  <div className="box-border hidden sm:flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between h-auto mt-[-10px] mb-[40px] sm:mb-[15px] w-full">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                      <div className="flex flex-row gap-2 items-center">
                        {selectedRows.length > 0 && (
                          <Menu
                            isOpen={editSelectedMenuOpen}
                            onOpenChange={setEditSelectedMenuOpen}
                            inverted
                            aria-label={t('timeline.table.editSelected')}
                            content={
                              <>
                                <MenuItem
                                  icon={<MagicWandIcon weight="duotone" size={20} />}
                                  onSelect={() => {
                                    handleOpenEnrichSelectedEventsDialog();
                                    recordStat(
                                      'EnrichSelectedEventsButtonClicked',
                                      undefined,
                                      statsContext,
                                    );
                                  }}
                                >
                                  {t('timeline.dialog.enrich')}
                                </MenuItem>
                                <MenuItem
                                  icon={<TrashIcon weight="duotone" size={20} />}
                                  destructive
                                  onSelect={() => {
                                    setDeleteSelectedEventsDialogOpen(true);
                                    recordStat(
                                      'DeleteSelectedEventsButtonClicked',
                                      undefined,
                                      statsContext,
                                    );
                                  }}
                                >
                                  {t('timeline.dialog.delete')}
                                </MenuItem>
                              </>
                            }
                          >
                            <Button
                              size="sm"
                              style="solid"
                              color="dark"
                              background="inverse"
                              aria-label={t('timeline.table.editSelected')}
                              aria-haspopup="menu"
                              trailingIcon={<CaretDownIcon />}
                              className={`justify-between min-w-[142.8px] ${
                                selectedSortColumn ? 'rounded-r-none border-r-0' : ''
                              }`}
                              onClick={() => setEditSelectedMenuOpen(!editSelectedMenuOpen)}
                            >
                              {t('timeline.table.editSelectedEvents')}
                            </Button>
                          </Menu>
                        )}
                        <Button
                          size="sm"
                          style="solid"
                          color="dark"
                          background="inverse"
                          data-testid="AddEventButton"
                          aria-label={t('timeline.table.addEventAria')}
                          className="gap-2"
                          tabIndex={0}
                          leadingIcon={<ListPlusIcon weight="duotone" />}
                          onClick={(event) => {
                            handleCreateEvent();
                            recordStat(
                              'AddEventButtonClicked',
                              event,
                              statsContext                            );
                          }}
                        >
                          {t('timeline.table.addEvent')}
                        </Button>
                        {isEditableTimeline && (
                          <>
                            <Menu
                              isOpen={sortColumnMenuOpen}
                              onOpenChange={setSortColumnMenuOpen}
                              inverted
                              aria-label={t('timeline.table.sortByColumnAria')}
                              content={
                                <>
                                  {columnDefs
                                    .filter((col) => 'sortable' in col && col.sortable !== false)
                                    .map((col) => (
                                      <MenuItem
                                        key={col.field || col.headerName || ''}
                                        onSelect={() =>
                                          handleSortColumnSelect(col.field || '')
                                        }
                                      >
                                        {col.headerName || col.field}
                                      </MenuItem>
                                    ))}
                                </>
                              }
                            >
                              <Button
                                size="sm"
                                style="solid"
                                color="dark"
                                background="inverse"
                                aria-label={t('timeline.table.sortByColumnAria')}
                                aria-haspopup="menu"
                                trailingIcon={<CaretDownIcon />}
                                className={`justify-between min-w-[142.8px] ${
                                  selectedSortColumn ? 'rounded-r-none border-r-0' : ''
                                }`}
                                onClick={() => setSortColumnMenuOpen(!sortColumnMenuOpen)}
                              >
                                {columnDefs.find((col) => col.field === selectedSortColumn)?.headerName || t('timeline.table.sortByColumn')}
                              </Button>
                            </Menu>
                            {selectedSortColumn && (
                              <Button
                                iconOnly
                                size="sm"
                                style="solid"
                                color="dark"
                                background="inverse"
                                aria-label={sortDirection === 'desc' ? t('timeline.table.sortAscending') : t('timeline.table.sortDescending')}
                                className="ml-[-10px] pl-[7px] pr-[6px] rounded-l-none"
                                onClick={handleSortDirectionToggle}
                              >
                                {sortDirection === 'desc' ? (
                                  <SortAscendingIcon />
                                ) : (
                                  <SortDescendingIcon />
                                )}
                              </Button>
                            )}
                            {applyOrderEnabled && (
                              <TpTooltip content={t('timeline.table.saveOrderHint')} placement="bottom" delayDuration={0}>
                                <div className="flex flex-row items-center h-full">
                                  <Button
                                    size="sm"
                                    style="solid"
                                    color="success"
                                    background="inverse"
                                    data-testid="ApplyOrderButton"
                                    className="gap-2 p-[8px]"
                                    onClick={(event) => {
                                      handleApplyOrder();
                                      recordStat(
                                        'ApplyOrderButtonClicked',
                                        event,
                                        statsContext                                      );
                                    }}
                                  >
                                    {t('timeline.table.saveOrder')}
                                  </Button>
                                </div>
                              </TpTooltip>
                            )}
                          </>
                        )}
                        {isEditableTimeline && eventsReviewModalExtractionJob && (
                          <EventsReviewModal
                            isOpen={eventsReviewModalOpen}
                            onClose={() => handleCloseEventsReviewModal(false)}
                            question={eventsReviewModalQuestion}
                            extractionJob={eventsReviewModalExtractionJob}
                            setApplyCardOpen={setEventsReviewModalOpen}
                            createEventsFromEntityEvents={createEventsFromEntityEvents}
                            getEventsFromEntityService={getEventsFromEntityService}
                          />
                        )}
                        {isEditableTimeline && (
                          <Menu
                            isOpen={extractedEventsPopoverOpen}
                            inverted={false}
                            placement="bottom-start"
                            maxWidth="31.25rem"
                            aria-label={t('timeline.table.extractionJobs')}
                            onOpenChange={(open) => {
                              if (open) {
                                openExtractedEvents();
                              } else {
                                setExtractedEventsPopoverOpen(false);
                              }
                            }}
                            content={
                              !loadingExtractedEvents && extractionJobs && extractionJobs.length > 0 ? (
                                <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
                                  {extractionJobs.map((job, index) => {
                                    const jobId = getExtractionJobId(job, index);
                                    const questionLabel = getExtractionJobQuestion(job, index);
                                    const isPending = isExtractionJobPending(job);

                                    return (
                                      <MenuItem
                                        key={jobId}
                                        disabled={isPending}
                                        onSelect={() => handleOpenExtractionJob(job, index)}
                                      >
                                        <span className="flex w-full items-center justify-between gap-2 text-left">
                                          <span className="whitespace-normal wrap-break-word">{questionLabel}</span>
                                          {isPending ? (
                                            <Spinner size="sm" color="white" variant="wave" />
                                          ) : (
                                            <CheckIcon size={20} />
                                          )}
                                        </span>
                                      </MenuItem>
                                    );
                                  })}
                                </div>
                              ) : loadingExtractedEvents ? (
                                <MenuItem disabled>
                                  <span className="flex w-full items-center justify-center gap-1">
                                    {t('timeline.table.checkingExtracted')}
                                    <Spinner size="sm" color="white" variant="wave" className="opacity-70" />
                                  </span>
                                </MenuItem>
                              ) : (
                                <MenuItem disabled>{t('timeline.table.noExtracted')}</MenuItem>
                              )
                            }
                          >
                            <Button
                              type="button"
                              size="sm"
                              style="solid"
                              color="dark"
                              background="inverse"
                              aria-label={t('timeline.table.extractedEvents')}
                              aria-haspopup="menu"
                              trailingIcon={<CaretDownIcon />}
                              className="justify-between min-w-[142.8px]"
                              onClick={() => {
                                if (extractedEventsPopoverOpen) {
                                  setExtractedEventsPopoverOpen(false);
                                } else {
                                  openExtractedEvents();
                                }
                              }}
                            >
                              {t('timeline.table.extractedEvents')}
                            </Button>
                          </Menu>
                        )}
                      </div>
                    </div>
                    {isEditableTimeline && (
                      <div className="hidden sm:flex flex-row items-end justify-end h-full w-[391px] gap-4">
                        {featureFlags?.exportTimelinePptx && (
                          <Button
                            id="ExportTimelinePptxButton"
                            size="sm"
                            style="solid"
                            color="dark"
                            background="inverse"
                            aria-label={exportingPptx ? t('timeline.table.exporting') : t('timeline.table.exportPptxAria')}
                            className="gap-2 disabled:opacity-60"
                            tabIndex={0}
                            leadingIcon={<DownloadSimpleIcon />}
                            isLoading={exportingPptx}
                            loadingText={t('timeline.table.exportingLabel')}
                            onClick={(event) => {
                              handleOpenPptxExportModal();
                              recordStat(
                                'ExportTimelinePptxButtonClicked',
                                event,
                                statsContext                            );
                            }}
                          >
                            {t('timeline.table.exportPptx')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          style="solid"
                          color="dark"
                          background="inverse"
                          data-testid="DeleteTimelineButton"
                          aria-label={t('timeline.table.deleteTimelineAria')}
                          className="gap-2"
                          tabIndex={0}
                          leadingIcon={<TrashIcon weight="duotone" />}
                          onClick={(event) => {
                            setDeleteTimelineDialogOpen(true);
                            recordStat(
                              'DeleteTimelineButtonClicked',
                              event,
                              statsContext                            );
                          }}
                        >
                          {t('timeline.table.deleteTimeline')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <div role="region" aria-label={t('timeline.table.region')} className="w-full h-full">
                  <AgGridReact
                    ref={gridRef}
                    onRowDragEnd={handleRowDragEnd}
                    rowDragManaged={true}
                    animateRows={true}
                    rowHeight={90}
                    // Keep extra rows mounted so fling-scrolls don't outrun virtualization.
                    rowBuffer={20}
                    // Draw rows synchronously while scrolling. AG Grid's default
                    // animation-frame path leaves temporarily empty rows (and can
                    // stick that way after a fast scroll to top/bottom).
                    suppressAnimationFrame={true}
                    rowData={allSlides as any}
                    getRowId={getRowId}
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs as any}
                    rowDragMultiRow={true}
                    rowSelection={ !isEditableTimeline ? undefined : {
                      mode: 'multiRow',
                      checkboxes: true,
                      headerCheckbox: true,
                    }}
                    onSortChanged={handleSortChanged}
                    onSelectionChanged={handleSelectionChanged}
                    onGridReady={checkDisplayOrder}
                    onFilterChanged={checkDisplayOrder}
                    gridOptions={{
                      suppressScrollOnNewData: true,
                      rowStyle: {
                        cursor: 'pointer',
                      },
                    }}
                    theme={myTheme}
                    localeText={gridLocaleText}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default React.memo(TPTable);
